// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { ChevronRight, X } from 'lucide-react'
import type * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { ListTable, toServerFilters, type ListTableCap, type ListTableColumn } from '../../components/common/ListTable'
import type { ColumnFilters, ServerFilterOptions } from '../../components/common/ColumnFilter'
import SearchableSelect from '../../components/common/SearchableSelect'
import SidePanel from '../../components/common/SidePanel'
import { ErrorBanner, formatTimestamp, relativeTime } from '../../components/workflow/StatePills'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useAuth } from '../../auth/AuthContext'
import {
  useAuditCatalog, useAuditEntry, useAuditPharmacies, useAuditSearch, useAuditSubjects,
} from '../../hooks/queries/audit'

// =====================================================================================
// D-AUDIT-2 PR C/D — the audit log you can investigate with.
//
// Every filter, the paging and the count run on the server (GET /api/audit/search). The page only
// collects intent. It replaces AuditTab, which fetched the newest 100-500 rows and filtered them
// in the browser, so nothing older than those rows could ever be found.
//
// Scope: a pharmacy admin sees their own pharmacy, always. SaaS admin and support pick ONE
// pharmacy before anything loads (or, SaaS admin only, Platform). Nothing lists two pharmacies.
// =====================================================================================

interface Props {
  /** SaaS admin surface: also offer the Platform scope (rows that belong to no pharmacy).
   *  Has no effect for a viewer bound to a pharmacy. */
  allowPlatform?: boolean
  /** Rendered at the right of the filter bar (e.g. the demo-reset control on /admin/audit). */
  toolbarExtra?: ReactNode
}

type Subject = { kind: 'patient' | 'rx'; id: string; label: string }
type Record_ = { type: string; id: string }

const CAPS: Record<string, ListTableCap> = {
  when:   { filterable: true, klass: 'date', ops: ['gte', 'lte'] },
  actor:  { filterable: true, ops: ['contains'] },
  action: { filterable: true, facetable: true, klass: 'enum', ops: ['in'] },
  entity: { filterable: true, facetable: true, klass: 'enum', ops: ['in'] },
}

const muted: CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const bar: CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: 12, marginBottom: 12,
  background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6,
}
const chip: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 12, fontSize: 12,
  background: 'var(--color-primary-soft, #e6f4f1)', border: '1px solid var(--color-border)',
}
const textInput: CSSProperties = {
  padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13,
  background: 'var(--color-surface)', fontFamily: 'inherit', minWidth: 240,
}
const linkBtn: CSSProperties = {
  border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: 12,
  color: 'var(--color-accent, #0f766e)', textDecoration: 'underline',
}

/** A JSON-rendered value, readable: strings lose their quotes, absent reads as a dash. */
const showValue = (v: string | null) => {
  if (v === null) return '—'
  if (v.length >= 2 && v.startsWith('"') && v.endsWith('"')) {
    try { return JSON.parse(v) as string } catch { return v }
  }
  return v
}

export default function AuditExplorer({ allowPlatform = false, toolbarExtra }: Props) {
  // The viewer decides, not the route: /admin/audit is reachable by a pharmacy admin too. A bound
  // viewer is their own pharmacy (the server enforces it regardless); anyone else picks one.
  const { me } = useAuth()
  const requirePharmacy = !me?.pharmacy_id
  const [pharmacyId, setPharmacyId] = useState('')
  const scopeReady = !requirePharmacy || pharmacyId !== ''
  const scopeArg = requirePharmacy ? pharmacyId : undefined

  const [colFilters, setColFilters] = useState<ColumnFilters>({})
  const [excludeSystem, setExcludeSystem] = useState(true)
  const [subject, setSubject] = useState<Subject | null>(null)
  const [record, setRecord] = useState<Record_ | null>(null)
  const [subjectQ, setSubjectQ] = useState('')
  const debouncedQ = useDebouncedValue(subjectQ, 300)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSizeRaw] = useState(() => preferredPageSize(25))
  const [openId, setOpenId] = useState<number | null>(null)
  const [showRaw, setShowRaw] = useState(false)

  const pharmacies = useAuditPharmacies(requirePharmacy)
  const catalog = useAuditCatalog(scopeArg, scopeReady)
  const subjects = useAuditSubjects(debouncedQ, scopeArg, scopeReady && pharmacyId !== 'platform' && !subject)

  const filter = useMemo(() => {
    const out = toServerFilters(colFilters, CAPS).serverFilters.map(f => `${f.key}:${f.op}:${f.values.join(',')}`)
    return out.sort()
  }, [colFilters])

  const args: api.AuditSearchArgs = {
    pharmacyId: scopeArg, page, pageSize, filter, excludeSystem,
    patientId: subject?.kind === 'patient' ? subject.id : undefined,
    rxNumber: subject?.kind === 'rx' ? subject.id : undefined,
    recordType: record?.type, recordId: record?.id,
  }
  const search = useAuditSearch(args, scopeReady)
  const entry = useAuditEntry(openId, scopeArg)

  // Any change to what is asked for returns to page 1.
  const resetPage = () => setPage(1)
  const onFilters = (next: ColumnFilters) => { setColFilters(next); resetPage() }
  const setPageSize = (n: number) => { rememberPageSize(n); setPageSizeRaw(n); resetPage() }

  const fetchOptions = async (colKey: string, term: string): Promise<ServerFilterOptions> => {
    const src = colKey === 'action' ? catalog.data?.actions : colKey === 'entity' ? catalog.data?.entity_types : undefined
    const t = term.trim().toLowerCase()
    const options = (src ?? [])
      .filter(o => !t || o.value.toLowerCase().includes(t))
      .map(o => ({ value: o.value, label: `${o.value} (${o.count.toLocaleString()})` }))
    return {
      options, source: 'distinct', truncated: false,
      notice: catalog.data ? `Seen in the last ${catalog.data.window_days} days in this scope.` : 'Loading…',
    }
  }

  const openEntry = (id: number) => { setOpenId(id); setShowRaw(false) }

  const columns: ListTableColumn<api.AuditRow>[] = [
    {
      // One click opens the entry, as the old page's expand arrow did. The shared table's own
      // row-open is a double-click (ListTable), which on this page read as "rows do nothing".
      key: 'open', label: '',
      render: r => (
        <button type="button" aria-label={`Open audit entry ${r.id}`} title="Open this entry"
          onClick={ev => { ev.stopPropagation(); openEntry(r.id) }}
          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-soft)' }}>
          <ChevronRight size={16} />
        </button>
      ),
    },
    {
      key: 'when', label: 'When',
      render: r => <><div>{relativeTime(r.occurred_at)}</div><div style={muted}>{formatTimestamp(r.occurred_at)}</div></>,
    },
    { key: 'actor', label: 'Who', render: r => <><div>{r.actor_account}</div><div style={muted}>{r.actor_role}</div></> },
    { key: 'action', label: 'Action', render: r => <code style={{ fontSize: 12 }}>{r.action}</code> },
    {
      key: 'entity', label: 'Record',
      render: r => <><div style={{ fontSize: 12 }}>{r.entity_type}</div>
        <div style={{ ...muted, fontFamily: 'monospace' }}>{r.entity_id.length > 13 ? r.entity_id.slice(0, 13) + '…' : r.entity_id}</div></>,
    },
    {
      key: 'about', label: 'About',
      render: r => [r.subject_rx && `Rx ${r.subject_rx}`, r.subject_patient].filter(Boolean).join(' · ') || <span style={muted}>—</span>,
    },
  ]

  const changeColumns: ListTableColumn<api.AuditFieldChange>[] = [
    { key: 'field', label: 'Field', render: c => <code style={{ fontSize: 12 }}>{c.field}</code> },
    { key: 'before', label: 'Before', render: c => <span style={{ whiteSpace: 'pre-wrap' }}>{showValue(c.before)}</span> },
    { key: 'after', label: 'After', render: c => <span style={{ whiteSpace: 'pre-wrap' }}>{showValue(c.after)}</span> },
  ]

  const pharmacyOptions = [
    ...(allowPlatform && requirePharmacy ? [{ value: 'platform', label: 'Platform (no pharmacy)', hint: 'heartbeats, platform operations' }] : []),
    ...(pharmacies.data?.items ?? []).map(p => ({ value: p.id, label: p.name, hint: p.city ?? undefined })),
  ]

  const data = search.data
  const e = entry.data

  return (
    <div>
      {requirePharmacy && (
        <div style={bar}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Pharmacy</span>
          <SearchableSelect
            value={pharmacyId}
            onChange={v => { setPharmacyId(v); setSubject(null); setRecord(null); setColFilters({}); resetPage() }}
            options={pharmacyOptions}
            placeholder={pharmacies.isLoading ? 'Loading pharmacies…' : 'Pick one pharmacy'}
            ariaLabel="Pharmacy"
            style={{ minWidth: 320 }} />
          <span style={muted}>Audit rows are shown one pharmacy at a time.</span>
        </div>
      )}

      {scopeReady && (
        <div style={bar}>
          {subject ? (
            <span style={chip}>
              About {subject.kind === 'rx' ? `Rx ${subject.label}` : subject.label}
              <button type="button" aria-label="Clear patient or Rx" style={linkBtn}
                onClick={() => { setSubject(null); resetPage() }}><X size={12} /></button>
            </span>
          ) : pharmacyId !== 'platform' && (
            <div style={{ position: 'relative' }}>
              <input type="search" value={subjectQ} onChange={ev => setSubjectQ(ev.target.value)}
                placeholder="About a patient (name, MRN) or an Rx number…" aria-label="About a patient or Rx"
                style={textInput} />
              {subjectQ.trim().length >= 2 && (
                <div role="listbox" style={{
                  position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 2,
                  background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 4,
                  boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,.1))', maxHeight: 260, overflowY: 'auto',
                }}>
                  {subjects.isFetching && <div style={{ ...muted, padding: 8 }}>Searching…</div>}
                  {subjects.isError && <div style={{ ...muted, padding: 8 }}>{friendlyError(subjects.error)}</div>}
                  {!subjects.isFetching && subjects.data?.items.length === 0 && <div style={{ ...muted, padding: 8 }}>No patient or Rx matches.</div>}
                  {subjects.data?.items.map(h => (
                    <button key={`${h.kind}:${h.id}`} type="button" role="option" aria-selected="false"
                      onClick={() => { setSubject({ kind: h.kind, id: h.id, label: h.label }); setSubjectQ(''); setRecord(null); resetPage() }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13 }}>
                      {h.kind === 'rx' ? `Rx ${h.label}` : h.label}
                      {h.dob && <span style={{ ...muted, marginLeft: 6 }}>DOB {formatTimestamp(h.dob).split(',')[0]}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {record && (
            <span style={chip}>
              History of {record.type} {record.id.slice(0, 13)}…
              <button type="button" aria-label="Clear record" style={linkBtn}
                onClick={() => { setRecord(null); resetPage() }}><X size={12} /></button>
            </span>
          )}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={excludeSystem} style={{ margin: 0 }}
              onChange={ev => { setExcludeSystem(ev.target.checked); resetPage() }} />
            Hide system events
          </label>
          <span style={{ flex: 1 }} />
          {toolbarExtra}
        </div>
      )}

      {!scopeReady && <p style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>Pick a pharmacy to see its audit log.</p>}

      {scopeReady && (
        <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 8px' }}>
          {search.isError && <ErrorBanner message={friendlyError(search.error)} />}
          <ListTable<api.AuditRow>
            viewKey="audit:search"
            columns={columns}
            rows={data?.items ?? []}
            loading={search.isLoading}
            getRowKey={r => String(r.id)}
            caps={CAPS}
            filters={colFilters}
            onFilters={onFilters}
            fetchOptions={fetchOptions}
            onRowOpen={r => openEntry(r.id)}
            rowTitle="Click › or double-click to open this entry"
            emptyText="No audit events match these filters." />
          {data && (
            <>
              <Pager page={data.page} totalPages={data.total_pages} total={data.total} pageSize={pageSize}
                onPage={setPage} onPageSize={setPageSize} label="events" />
              {data.truncated && (
                <p style={{ ...muted, textAlign: 'right', margin: '0 4px 8px' }}>
                  More than {data.total.toLocaleString()} events match; narrow by date, action or patient to count them all.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <SidePanel open={openId !== null} onClose={() => setOpenId(null)} width={760}
        title={e ? e.action : 'Audit entry'}
        subtitle={e ? `${formatTimestamp(e.occurred_at)} · ${e.actor_account} (${e.actor_role})` : null}>
        {entry.isLoading && <p style={muted}>Loading…</p>}
        {entry.isError && <ErrorBanner message={friendlyError(entry.error)} />}
        {e && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ fontSize: 13 }}>
              <div><b>Record</b> {e.entity_type} <code style={{ fontSize: 12 }}>{e.entity_id}</code></div>
              <div style={muted}>audit id {e.id} · session {e.actor_session ?? '—'} · ip {e.actor_ip ?? '—'}</div>
              <button type="button" style={{ ...linkBtn, marginTop: 6 }}
                onClick={() => { setRecord({ type: e.entity_type, id: e.entity_id }); setSubject(null); setOpenId(null); resetPage() }}>
                Show every event for this record
              </button>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                What changed {e.changes.length > 0 && <span style={muted}>({e.changes.length} field{e.changes.length === 1 ? '' : 's'})</span>}
              </div>
              {e.changes.length === 0
                ? <p style={muted}>No field-level change recorded for this event (a read, a sign-in, or a create/delete without states).</p>
                : <ListTable<api.AuditFieldChange> viewKey="audit:changes" columns={changeColumns} rows={e.changes}
                    getRowKey={c => c.field} emptyText="No changes." />}
            </div>
            <div>
              <button type="button" style={linkBtn} onClick={() => setShowRaw(v => !v)}>
                {showRaw ? 'Hide' : 'Show'} the full before / after records
              </button>
              {showRaw && (['before_state', 'after_state', 'metadata'] as const).map(k => (
                <div key={k} style={{ marginTop: 8 }}>
                  <div style={{ ...muted, fontWeight: 700, textTransform: 'uppercase' }}>{k.replace('_state', '')}</div>
                  <pre style={{ margin: 0, padding: 8, fontSize: 11, maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                    {prettyJson(e[k])}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  )
}

function prettyJson(s: string | null): string {
  if (!s) return '(none)'
  try { return JSON.stringify(JSON.parse(s), null, 2) } catch { return s }
}
